import { apiClient, isHttpClientError } from "@/http";
import type { UserRole } from "@/app/userRole";
import { apiRoutes } from "./apiRoutes";

// 관리자용 조직 멤버 목록과 등급 변경 응답. display_name은 연결된 User의 이름이고,
// team_names는 GitHub 팀 멤버십이다. 등급은 admin·member 중 하나다.
export type Member = {
  uuid: string;
  display_name: string;
  tier: UserRole;
  // 계정 종류다. human 은 GitHub 로 로그인하는 사람이다. system 은 사람이 아닌 행위자다
  // (예: Hatchery·Sebang). 시스템 Member 는 training API 를 부를 수 없다. 등급은 항상 member 다.
  // 구 BE 응답에는 이 키가 없다. 그래서 배포 기간 동안 optional 이다.
  // @compat ZERG-1074 until=be:2689d1bb
  account_type?: "human" | "system";
  team_names: readonly string[];
  created_at: string;
  modified_at: string;
};

// 다른 리소스가 가리키는 Member 참조(uuid + 표시명). backend MemberRefResp 의 미러다.
// Dataset 담당자(operator_member) 참조와 담당자 후보 목록이 이 타입을 함께 쓴다.
export type MemberRef = {
  uuid: string;
  display_name: string;
};

export type MemberTierUpdateReq = {
  tier: UserRole;
};

// 조직 멤버용 담당자 후보. 표시 이름·이메일 순서는 backend가 보장한다.
export async function listOrganizationMembers(): Promise<MemberRef[]> {
  const response = await apiClient.get<MemberRef[]>(apiRoutes.members);
  return response.data;
}

// admin 전용. organization member 목록. backend 가 display_name 오름차순을 보장한다.
export async function listMembers(): Promise<Member[]> {
  const response = await apiClient.get<Member[]>(apiRoutes.admin.members);
  return response.data;
}

// admin 전용. 멤버 등급을 설정한다(admin grant/revoke 포함).
// 마지막 admin 을 강등하면 409 member.last_admin 이 온다.
export async function setMemberTier(memberUuid: string, tier: UserRole): Promise<Member> {
  const response = await apiClient.patch<Member>(apiRoutes.admin.memberDetail(memberUuid), {
    tier,
  } satisfies MemberTierUpdateReq);
  return response.data;
}

// setMemberTier 가 "마지막 admin 강등 차단"(409)으로 실패했는지 판정한다.
const LAST_ADMIN_CODE = "member.last_admin";
export function isLastAdminError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === LAST_ADMIN_CODE;
}
